import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IArtist,
  IArtistPaginationResult,
  IArtistEditorialLocale,
  IArtistEditorial,
  IArtistEditorialPaginationResult,
  IArtistAward,
  IArtistAwardListResult,
  IArtistCollection,
  IArtistCollectionListResult,
  IArtistDegree,
  IArtistDegreeListResult,
  IArtistExhibition,
  IArtistExhibitionListResult,
  IArtistBibliography,
  IArtistBibliographyListResult,
  IArtistImage,
  IArtistLocale,
  IArtistFlat,
  IArtistFlatPaginationResult,
  IArtistFeedLocale,
  IArtistFeed,
  IArtistFeedPaginationResult,
  IArtistRanking,
} from '../interfaces/Artist.js';
import { Image, ImageHierarchical } from './Image.js';
import { PaginationResult, ListResult } from './List.js';
import { Author } from './User.js';

/**
 * ArtistEditorialLocale
 */
class ArtistEditorialLocale extends ABaseModel implements IArtistEditorialLocale {
  id: number;
  localeCode: string;
  text: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistRanking
 */
class ArtistRanking extends ABaseModel implements IArtistRanking {
  regionCode: string;
  popularity: number;
  featured: number;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistEditorial
 */
class ArtistEditorial extends ABaseModel implements IArtistEditorial {
  id: number;
  artistId: number;
  type: string;
  localizable: boolean;
  text: string;
  authorName: string;
  author: Author;
  locales: Array<ArtistEditorialLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistEditorialPaginationResult
 */
class ArtistEditorialPaginationResult extends PaginationResult implements IArtistEditorialPaginationResult {
  declare items: Array<ArtistEditorial>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * Artist
 */
class Artist extends ABaseModel implements IArtist {
  id: number;
  status: number;
  active: boolean;
  canApprove: boolean;
  canDisapprove: boolean;
  created: string;
  userId: number;
  alias: string;
  aliasUpdated: boolean;
  name: string;
  gender: string;
  website: string;
  commissions: boolean;
  statement: string;
  biography: string;
  biographyLocked: boolean;
  birthYear: number;
  birthCity: string;
  birthCountry: string;
  livesCity: string;
  livesCountry: string;
  instagramUsername: string;
  socialProfiles: string;
  soldInfo: string;
  press: string;
  galleries: string;
  score: number;
  artStatusMessage: string;
  sellerId: number;
  sellerName: string;
  sellerStore: string;
  sellerCurrency: string;
  sellerShipsCountryCode: string;
  statExhibiting: boolean;
  statArtCountActive: number;
  statArtCountVisible: number;
  statPriceMean: number;
  statPriceMedian: number;
  mediums: Array<string>;
  styles: Array<string>;
  regions: Array<string>;
  careerStages: Array<string>;
  images: Array<ArtistImage>;
  rankings: Array<ArtistRanking>;
  locales: Array<ArtistLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistPaginationResult
 */
class ArtistPaginationResult extends PaginationResult implements IArtistPaginationResult {
  declare items: Array<Artist>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistAward
 */
class ArtistAward extends ABaseModel implements IArtistAward {
  id: number;
  artistId: number;
  name: string;
  body: string;
  year: number;
  city: string;
  countryCode: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistAwardListResult
 */
class ArtistAwardListResult extends ListResult implements IArtistAwardListResult {
  declare items: Array<ArtistAward>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistCollection
 */
class ArtistCollection extends ABaseModel implements IArtistCollection {
  id: number;
  artistId: number;
  collection: string;
  city: string;
  countryCode: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistCollectionListResult
 */
class ArtistCollectionListResult extends ListResult implements IArtistCollectionListResult {
  declare items: Array<ArtistCollection>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistDegree
 */
class ArtistDegree extends ABaseModel implements IArtistDegree {
  id: number;
  artistId: number;
  description: string;
  university: string;
  city: string;
  year: number;
  countryCode: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistDegreeListResult
 */
class ArtistDegreeListResult extends ListResult implements IArtistDegreeListResult {
  declare items: Array<ArtistDegree>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistExhibition
 */
class ArtistExhibition extends ABaseModel implements IArtistExhibition {
  id: number;
  artistId: number;
  title: string;
  type: string;
  venue: string;
  year: number;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistExhibitionListResult
 */
class ArtistExhibitionListResult extends ListResult implements IArtistExhibitionListResult {
  declare items: Array<ArtistExhibition>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistBibliography
 */
class ArtistBibliography extends ABaseModel implements IArtistBibliography {
  id: number;
  artistId: number;
  title: string;
  author: string;
  publication: string;
  publisher: string;
  year: number;
  pages: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistBibliographyListResult
 */
class ArtistBibliographyListResult extends ListResult implements IArtistBibliographyListResult {
  declare items: Array<ArtistBibliography>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistImage
 */
class ArtistImage extends ImageHierarchical implements IArtistImage {
  caption: string;
  declare children: Array<ArtistImage>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistLocale
 */
class ArtistLocale extends ABaseModel implements IArtistLocale {
  id: number;
  localeCode: string;
  biography: string;
  statement: string;
  artStatusMessage: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistFlat
 */
class ArtistFlat extends ABaseModel implements IArtistFlat {
  id: number;
  status: number;
  active: boolean;
  name: string;
  alias: string;
  gender: string;
  website: string;
  score: number;
  commissions: boolean;
  statement: string;
  biography: string;
  biographyLocked: boolean;
  bioIsRichContent: boolean;
  birthYear: number;
  birthCity: string;
  birthCountry: string;
  livesCity: string;
  livesCountry: string;
  locales: Array<ArtistLocale>;
  images: Array<ArtistImage>;
  mediums: Array<string>;
  styles: Array<string>;
  careerStages: Array<string>;
  hasArticles: boolean;
  hasAwards: boolean;
  hasBibliography: boolean;
  hasCollections: boolean;
  hasDegrees: boolean;
  hasExhibitions: boolean;
  hasEvents: boolean;
  hasFeed: boolean;
  sellerId: number;
  sellerStatus: number;
  sellerShipsCountry: string;
  userId: number;
  userStatus: number;
  userRole: string;
  userLastLogin: string;
  userRegistrationDate: string;
  metaId: number;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  metaPageTitle: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistFlatPaginationResult
 */
class ArtistFlatPaginationResult extends PaginationResult implements IArtistFlatPaginationResult {
  declare items: Array<ArtistFlat>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistFeedLocale
 */
class ArtistFeedLocale extends ABaseModel implements IArtistFeedLocale {
  id: number;
  localeCode: string;
  title: string;
  text: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistFeed
 */
class ArtistFeed extends ABaseModel implements IArtistFeed {
  id: number;
  artistId: number;
  status: number;
  title: string;
  text: string;
  publishedDate: string;
  mediaType: string;
  mediaPlatform: string;
  mediaId: string;
  images: Array<Image>;
  locales: Array<ArtistFeedLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtistFeedPaginationResult
 */
class ArtistFeedPaginationResult extends PaginationResult implements IArtistFeedPaginationResult {
  declare items: Array<ArtistFeed>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

export {
  ArtistEditorialLocale,
  ArtistEditorial,
  ArtistEditorialPaginationResult,
  Artist,
  ArtistPaginationResult,
  ArtistAward,
  ArtistAwardListResult,
  ArtistDegree,
  ArtistDegreeListResult,
  ArtistCollection,
  ArtistCollectionListResult,
  ArtistExhibition,
  ArtistExhibitionListResult,
  ArtistBibliography,
  ArtistBibliographyListResult,
  ArtistImage,
  ArtistLocale,
  ArtistRanking,
  ArtistFlat,
  ArtistFlatPaginationResult,
  ArtistFeedLocale,
  ArtistFeed,
  ArtistFeedPaginationResult,
};
